import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);
  const categoryArticles = articles.filter(a => a.category === slug);
  const catMap = Object.fromEntries(categories.map(c => [c.slug, c.name]));

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-800 mb-2">分类不存在</h1>
            <p className="text-sm text-foreground-500 mb-4">你要找的分类页面不存在或已被移除</p>
            <Link to="/" className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const featuredArticle = categoryArticles[0];
  const restArticles = categoryArticles.slice(1);

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 lg:px-10 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/10">
                <i className={`${category.icon} text-primary-500 text-lg`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {categoryArticles.length === 0 ? (
          <section className="w-full px-4 md:px-6 lg:px-10 py-20">
            <div className="max-w-7xl mx-auto text-center">
              <i className="ri-inbox-line text-5xl text-foreground-400 mb-4 block"></i>
              <p className="text-sm text-foreground-500">该分类下暂无内容，敬请期待</p>
            </div>
          </section>
        ) : (
          <>
            {featuredArticle && (
              <section className="w-full px-4 md:px-6 lg:px-10 pb-8 md:pb-12">
                <div className="max-w-7xl mx-auto">
                  <ArticleCard
                    slug={featuredArticle.slug}
                    title={featuredArticle.title}
                    excerpt={featuredArticle.excerpt}
                    imageUrl={featuredArticle.imageUrl}
                    category={featuredArticle.category}
                    categoryName={catMap[featuredArticle.category] || featuredArticle.category}
                    publishedAt={featuredArticle.publishedAt}
                    views={featuredArticle.views}
                    comments={featuredArticle.comments}
                    isHot={featuredArticle.isHot}
                    variant="featured"
                  />
                </div>
              </section>
            )}

            {restArticles.length > 0 && (
              <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-100/50">
                <div className="max-w-7xl mx-auto">
                  <h2 className="text-lg font-semibold text-foreground-700 mb-6 font-label tracking-wide uppercase">
                    更多{category.name}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {restArticles.map((article) => (
                      <ArticleCard
                        key={article.slug}
                        slug={article.slug}
                        title={article.title}
                        excerpt={article.excerpt}
                        imageUrl={article.imageUrl}
                        category={article.category}
                        categoryName={catMap[article.category] || article.category}
                        publishedAt={article.publishedAt}
                        views={article.views}
                        comments={article.comments}
                        isHot={article.isHot}
                      />
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        <section className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-semibold text-foreground-700 mb-4 font-label tracking-wide uppercase">
              探索其他分类
            </h2>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.filter(c => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 hover:bg-primary-500/10 border border-background-200/30 hover:border-primary-500/30 text-sm text-foreground-600 hover:text-primary-400 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  <span>{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name} - 51暗网`,
          "url": `https://www.51darknet.com/category/${category.slug}`,
          "description": category.description,
          "isPartOf": {
            "@type": "WebSite",
            "name": "51暗网",
            "url": "https://www.51darknet.com"
          },
          "inLanguage": "zh-CN"
        })
      }} />
    </>
  );
}